import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { shiftAssignmentAPI, shiftAPI, employeeAPI, departmentAPI } from '../../api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Calendar, Clock, Users, Plus, Trash2 } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { format } from 'date-fns';
import { toast } from 'sonner';

const ShiftAssignmentPage = () => {
  const { user } = useAuth();
  const companyId = user?.company_id;
  
  const [assignments, setAssignments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Single assignment dialog state
  const [singleDialogOpen, setSingleDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedShift, setSelectedShift] = useState('');
  const [assignmentType, setAssignmentType] = useState('default');
  const [effectiveDate, setEffectiveDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  
  // Bulk assignment dialog state
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkSelectedEmployees, setBulkSelectedEmployees] = useState([]);
  const [bulkSelectedShift, setBulkSelectedShift] = useState('');
  const [bulkAssignmentType, setBulkAssignmentType] = useState('default');
  const [bulkEffectiveDate, setBulkEffectiveDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  
  // Filter state
  const [filterDepartment, setFilterDepartment] = useState('all');

  const loadData = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const [assignRes, empRes, shiftRes, deptRes] = await Promise.all([
        shiftAssignmentAPI.getAll(companyId),
        employeeAPI.getAll(companyId),
        shiftAPI.getAll(companyId),
        departmentAPI.getAll(companyId)
      ]);
      setAssignments(assignRes.data);
      setEmployees(empRes.data);
      setShifts(shiftRes.data);
      setDepartments(deptRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSingleAssignment = async () => {
    if (!selectedEmployee || !selectedShift) {
      toast.error('Please select an employee and shift');
      return;
    }
    
    try {
      await shiftAssignmentAPI.create(companyId, {
        employee_id: selectedEmployee,
        shift_template_id: selectedShift,
        assignment_type: assignmentType,
        effective_date: effectiveDate
      });
      
      toast.success('Shift assigned successfully');
      setSingleDialogOpen(false);
      setSelectedEmployee('');
      setSelectedShift('');
      loadData();
    } catch (error) {
      console.error('Error assigning shift:', error);
      toast.error('Failed to assign shift');
    }
  };

  const handleBulkAssignment = async () => {
    if (bulkSelectedEmployees.length === 0 || !bulkSelectedShift) {
      toast.error('Please select at least one employee and a shift');
      return;
    }
    
    try {
      await shiftAssignmentAPI.bulkCreate(companyId, {
        employee_ids: bulkSelectedEmployees,
        shift_template_id: bulkSelectedShift,
        assignment_type: bulkAssignmentType,
        effective_date: bulkEffectiveDate
      });
      
      toast.success(`Shift assigned to ${bulkSelectedEmployees.length} employees`);
      setBulkDialogOpen(false);
      setBulkSelectedEmployees([]);
      setBulkSelectedShift('');
      loadData();
    } catch (error) {
      console.error('Error bulk assigning shifts:', error);
      toast.error('Failed to bulk assign shifts');
    }
  };

  const handleDeleteAssignment = async (assignmentId) => {
    try {
      await shiftAssignmentAPI.delete(assignmentId);
      toast.success('Assignment removed');
      loadData();
    } catch (error) {
      console.error('Error deleting assignment:', error);
      toast.error('Failed to remove assignment');
    }
  };

  const toggleBulkEmployee = (employeeId) => {
    setBulkSelectedEmployees(prev => 
      prev.includes(employeeId)
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const selectAllEmployees = () => {
    const filteredEmps = filteredEmployees.map(e => e.id);
    if (bulkSelectedEmployees.length === filteredEmps.length) {
      setBulkSelectedEmployees([]);
    } else {
      setBulkSelectedEmployees(filteredEmps);
    }
  };

  // Get employees without current shift assignments
  const unassignedEmployees = employees.filter(emp => 
    !assignments.some(a => a.employee_id === emp.id)
  );

  // Filter employees by department
  const filteredEmployees = filterDepartment === 'all' 
    ? employees 
    : employees.filter(e => e.department_id === filterDepartment);

  // Get department name for display
  const getDepartmentName = (deptId) => {
    const dept = departments.find(d => d.id === deptId);
    return dept?.name || 'Unassigned';
  };

  return (
    <div className="space-y-6" data-testid="shift-assignment-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Shift Assignment</h1>
          <p className="text-muted-foreground">Assign and manage employee shifts</p>
        </div>
        <div className="flex gap-2">
          {/* Single Assignment Dialog */}
          <Dialog open={singleDialogOpen} onOpenChange={setSingleDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="single-assign-btn">
                <Plus className="h-4 w-4" />
                Assign Shift
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Assign Shift to Employee</DialogTitle>
                <DialogDescription>
                  Select an employee and assign them to a shift
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Employee</Label>
                  <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                    <SelectTrigger data-testid="select-employee">
                      <SelectValue placeholder="Select employee" />
                    </SelectTrigger>
                    <SelectContent>
                      {employees.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.first_name} {emp.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Shift</Label>
                  <Select value={selectedShift} onValueChange={setSelectedShift}>
                    <SelectTrigger data-testid="select-shift">
                      <SelectValue placeholder="Select shift" />
                    </SelectTrigger>
                    <SelectContent>
                      {shifts.map((shift) => (
                        <SelectItem key={shift.id} value={shift.id}>
                          {shift.name} ({shift.start_time} - {shift.end_time})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Assignment Type</Label>
                  <Select value={assignmentType} onValueChange={setAssignmentType}>
                    <SelectTrigger data-testid="select-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">Default</SelectItem>
                      <SelectItem value="rotating">Rotating</SelectItem>
                      <SelectItem value="flexible">Flexible</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Effective Date</Label>
                  <Input 
                    type="date" 
                    value={effectiveDate} 
                    onChange={(e) => setEffectiveDate(e.target.value)}
                    data-testid="effective-date"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setSingleDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleSingleAssignment} data-testid="confirm-assign-btn">Assign</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Bulk Assignment Dialog */}
          <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="bulk-assign-btn">
                <Users className="h-4 w-4" />
                Bulk Assign
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Bulk Shift Assignment</DialogTitle>
                <DialogDescription>
                  Select multiple employees to assign the same shift
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Shift</Label>
                    <Select value={bulkSelectedShift} onValueChange={setBulkSelectedShift}>
                      <SelectTrigger data-testid="bulk-select-shift">
                        <SelectValue placeholder="Select shift" />
                      </SelectTrigger>
                      <SelectContent>
                        {shifts.map((shift) => (
                          <SelectItem key={shift.id} value={shift.id}>
                            {shift.name} ({shift.start_time} - {shift.end_time})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Assignment Type</Label>
                    <Select value={bulkAssignmentType} onValueChange={setBulkAssignmentType}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="default">Default</SelectItem>
                        <SelectItem value="rotating">Rotating</SelectItem>
                        <SelectItem value="flexible">Flexible</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Effective Date</Label>
                    <Input 
                      type="date" 
                      value={bulkEffectiveDate} 
                      onChange={(e) => setBulkEffectiveDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Filter by Department</Label>
                    <Select value={filterDepartment} onValueChange={setFilterDepartment}>
                      <SelectTrigger>
                        <SelectValue placeholder="All Departments" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Departments</SelectItem>
                        {departments.map((dept) => (
                          <SelectItem key={dept.id} value={dept.id}>
                            {dept.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Select Employees ({bulkSelectedEmployees.length} selected)</Label>
                    <Button variant="ghost" size="sm" onClick={selectAllEmployees}>
                      {bulkSelectedEmployees.length === filteredEmployees.length ? 'Deselect All' : 'Select All'}
                    </Button>
                  </div>
                  <ScrollArea className="h-[300px] border rounded-md p-2">
                    <div className="space-y-2">
                      {filteredEmployees.map((emp) => (
                        <div 
                          key={emp.id} 
                          className="flex items-center gap-3 p-2 hover:bg-muted rounded-md cursor-pointer"
                          onClick={() => toggleBulkEmployee(emp.id)}
                        >
                          <Checkbox 
                            checked={bulkSelectedEmployees.includes(emp.id)}
                            onCheckedChange={() => toggleBulkEmployee(emp.id)}
                          />
                          <div className="flex-1">
                            <p className="font-medium">{emp.first_name} {emp.last_name}</p>
                            <p className="text-sm text-muted-foreground">
                              {getDepartmentName(emp.department_id)} • {emp.employee_id || 'No ID'}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setBulkDialogOpen(false)}>Cancel</Button>
                <Button 
                  onClick={handleBulkAssignment} 
                  disabled={bulkSelectedEmployees.length === 0 || !bulkSelectedShift}
                  data-testid="confirm-bulk-assign-btn"
                >
                  Assign to {bulkSelectedEmployees.length} Employees
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Employees</p>
                <p className="text-2xl font-bold">{employees.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <Calendar className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Assigned Shifts</p>
                <p className="text-2xl font-bold">{assignments.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-orange-100 rounded-lg">
                <Clock className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Unassigned</p>
                <p className="text-2xl font-bold">{unassignedEmployees.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Unassigned Employees Alert */}
      {unassignedEmployees.length > 0 && (
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Clock className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="font-medium text-orange-800">
                  {unassignedEmployees.length} employees without shift assignments
                </p>
                <p className="text-sm text-orange-600 mt-1">
                  {unassignedEmployees.slice(0, 3).map(e => `${e.first_name} ${e.last_name}`).join(', ')}
                  {unassignedEmployees.length > 3 && ` and ${unassignedEmployees.length - 3} more...`}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Current Assignments Table */}
      <Card>
        <CardHeader>
          <CardTitle>Current Shift Assignments</CardTitle>
          <CardDescription>View and manage employee shift assignments</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Shift</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Effective From</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {assignments.map((assignment) => (
                  <TableRow key={assignment.id} data-testid="assignment-row">
                    <TableCell className="font-medium">{assignment.employee_name}</TableCell>
                    <TableCell>
                      <div>
                        <p>{assignment.shift_name}</p>
                        <p className="text-sm text-muted-foreground">{assignment.shift_time}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {assignment.assignment_type}
                      </Badge>
                    </TableCell>
                    <TableCell>{assignment.effective_date}</TableCell>
                    <TableCell>
                      <Badge variant={assignment.is_active ? 'default' : 'secondary'}>
                        {assignment.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDeleteAssignment(assignment.id)}
                        data-testid="delete-assignment-btn"
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {assignments.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      No shift assignments found. Assign shifts to employees to get started.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ShiftAssignmentPage;
